import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Search, FileText, Download, Copy, Ban, Loader2, AlertCircle } from 'lucide-react';
import { useApi } from '@/hooks/useApi';
import { api } from '@/lib/api';
import { formatDateTime } from '@/lib/utils';
import { ContratoLigero, ESTADO_CONTRATO, EstadoContrato, NOMBRE_MODELO, ClavePlantilla } from '@/lib/contratos';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';

export function EstadoContratoBadge({ estado }: { estado: EstadoContrato }) {
  const c = ESTADO_CONTRATO[estado] || { label: estado, className: 'bg-gray-100 text-gray-600' };
  return <span className={`badge ${c.className}`}>{c.label}</span>;
}

export function enlaceParte(c: Pick<ContratoLigero, 'parte' | 'ownerId' | 'leadId'>): string {
  if (c.ownerId) return `/owners/${c.ownerId}?tab=contratos`;
  if (c.parte.tipo === 'owner') return `/owners/${c.parte.id}?tab=contratos`;
  return '/leads/propietarios';
}

export default function ContratosListaPage() {
  const navigate = useNavigate();
  const [busqueda, setBusqueda] = useState('');
  // ?estado=enviado_firma llega desde el Dashboard («Contratos esperando firma»)
  const [searchParams] = useSearchParams();
  const [estado, setEstado] = useState<'' | EstadoContrato>(() => {
    const e = searchParams.get('estado');
    return e && e in ESTADO_CONTRATO ? (e as EstadoContrato) : '';
  });
  const [modelo, setModelo] = useState<'' | ClavePlantilla>('');
  const [ocupado, setOcupado] = useState<string | null>(null);

  const { data, loading, error, refresh } = useApi<ContratoLigero[]>(() => api.getContratos(), []);

  const filas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return (data || []).filter(c =>
      (!estado || c.estado === estado) &&
      (!modelo || c.templateKey === modelo) &&
      (!q || c.codigo.toLowerCase().includes(q) || c.parte.nombre.toLowerCase().includes(q) || (c.preajusteNombre || '').toLowerCase().includes(q))
    );
  }, [data, busqueda, estado, modelo]);

  const duplicar = async (c: ContratoLigero) => {
    setOcupado(c.id);
    try {
      const nuevo = await api.duplicarContrato(c.id);
      navigate(`/contracts/c/${nuevo.id}`);
    } catch (err: any) {
      alert(err.message || 'No se pudo duplicar');
    } finally {
      setOcupado(null);
    }
  };

  const anular = async (c: ContratoLigero) => {
    const motivo = prompt(`¿Anular el contrato ${c.codigo}? Puedes indicar el motivo:`);
    if (motivo === null) return;
    setOcupado(c.id);
    try {
      await api.anularContrato(c.id, motivo || undefined);
      refresh();
    } catch (err: any) {
      alert(err.message || 'No se pudo anular');
    } finally {
      setOcupado(null);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex flex-wrap items-center gap-2 flex-1">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-sereno-dark/30" />
            <input className="input pl-9" placeholder="Buscar por código, nombre o preajuste…" value={busqueda} onChange={e => setBusqueda(e.target.value)} />
          </div>
          <select className="input w-auto" value={estado} onChange={e => setEstado(e.target.value as any)}>
            <option value="">Todos los estados</option>
            {(Object.keys(ESTADO_CONTRATO) as EstadoContrato[]).map(k => <option key={k} value={k}>{ESTADO_CONTRATO[k].label}</option>)}
          </select>
          <select className="input w-auto" value={modelo} onChange={e => setModelo(e.target.value as any)}>
            <option value="">Todos los modelos</option>
            {(Object.keys(NOMBRE_MODELO) as ClavePlantilla[]).map(k => <option key={k} value={k}>{NOMBRE_MODELO[k]}</option>)}
          </select>
        </div>
        <Link to="/contracts/nuevo" className="btn-primary text-sm flex items-center gap-1.5">
          <Plus size={16} /> Nuevo contrato
        </Link>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <div className="card border border-red-200 bg-red-50 p-4 flex items-start gap-3">
          <AlertCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-red-700">No se pudieron cargar los contratos</p>
            <p className="text-sm text-red-600">{error}</p>
          </div>
        </div>
      ) : filas.length === 0 ? (
        <EmptyState
          title={data && data.length ? 'Ningún contrato coincide con el filtro' : 'Todavía no hay contratos generados'}
          description="Crea el primero desde «Nuevo contrato»: eliges propietario o lead, modelo y condiciones, y el PDF queda guardado en su ficha."
          action={<Link to="/contracts/nuevo" className="btn-primary text-sm inline-flex items-center gap-1.5"><Plus size={16} /> Nuevo contrato</Link>}
        />
      ) : (
        <div className="card p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-sereno-dark/40 border-b border-gray-100">
                  <th className="px-4 py-3 font-semibold">Código</th>
                  <th className="px-4 py-3 font-semibold">Parte</th>
                  <th className="px-4 py-3 font-semibold">Modelo</th>
                  <th className="px-4 py-3 font-semibold">Condiciones</th>
                  <th className="px-4 py-3 font-semibold">Estado</th>
                  <th className="px-4 py-3 font-semibold">Fecha</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {filas.map(c => (
                  <tr key={c.id} className="border-b border-gray-50 hover:bg-sereno-blue/10 cursor-pointer" onClick={() => navigate(`/contracts/c/${c.id}`)}>
                    <td className="px-4 py-3 font-mono text-xs font-semibold whitespace-nowrap">{c.codigo}</td>
                    <td className="px-4 py-3">
                      <Link to={enlaceParte(c)} onClick={e => e.stopPropagation()} className="font-medium hover:underline">{c.parte.nombre}</Link>
                      <span className="ml-2 text-[11px] uppercase tracking-wide text-sereno-dark/40">{c.ownerId || c.parte.tipo === 'owner' ? 'propietario' : 'lead'}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">{NOMBRE_MODELO[c.templateKey]} <span className="text-sereno-dark/40 text-xs">v{c.templateVersion}</span></td>
                    <td className="px-4 py-3 text-sereno-dark/70">
                      {c.preajusteNombre || 'Estándar'}
                      {Object.keys(c.sobrescrituras || {}).length + (c.clausulasExtra || []).length > 0 && (
                        <span className="ml-2 badge bg-amber-100 text-amber-800">retoques</span>
                      )}
                    </td>
                    <td className="px-4 py-3"><EstadoContratoBadge estado={c.estado} /></td>
                    <td className="px-4 py-3 text-sereno-dark/60 whitespace-nowrap">{formatDateTime(c.generadoAt || c.createdAt)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                        {c.pdf && (
                          <a href={api.contratoPdfUrl(c.id)} target="_blank" rel="noreferrer" className="p-1.5 rounded hover:bg-sereno-blue/30 text-sereno-dark/40 hover:text-sereno-dark" title="Ver PDF"><FileText size={15} /></a>
                        )}
                        {c.pdf && (
                          <a href={api.contratoPdfUrl(c.id, { download: true })} className="p-1.5 rounded hover:bg-sereno-blue/30 text-sereno-dark/40 hover:text-sereno-dark" title="Descargar"><Download size={15} /></a>
                        )}
                        <button onClick={() => duplicar(c)} disabled={ocupado === c.id} className="p-1.5 rounded hover:bg-sereno-blue/30 text-sereno-dark/40 hover:text-sereno-dark" title="Duplicar como borrador">
                          {ocupado === c.id ? <Loader2 size={15} className="animate-spin" /> : <Copy size={15} />}
                        </button>
                        {c.estado !== 'anulado' && c.estado !== 'borrador' && (
                          <button onClick={() => anular(c)} disabled={ocupado === c.id} className="p-1.5 rounded hover:bg-red-50 text-sereno-dark/40 hover:text-red-600" title="Anular"><Ban size={15} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
